import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useToast } from '../components/ui/use-toast';
import EmailPreview from '../components/email/EmailPreview';
import OrderConfirmationEmail from '../emails/templates/OrderConfirmationEmail';
import PurchaseConfirmationEmail from '../emails/templates/PurchaseConfirmationEmail';
import ClassConfirmationEmail from '../emails/templates/ClassConfirmationEmail';
import ClassConfirmationEmailV2 from '../emails/templates/ClassConfirmationEmailV2';
import ClassConfirmationEmailV3 from '../emails/templates/ClassConfirmationEmailV3';
import CertificateParticipationEmail from '../emails/templates/CertificateParticipationEmail';
import ClassReminderEmail from '../emails/templates/ClassReminderEmail';
import ClassCancellationEmail from '../emails/templates/ClassCancellationEmail';
import InvoiceEmail from '../emails/templates/InvoiceEmail';
import ClassRescheduledEmail from '../emails/templates/ClassRescheduledEmail';
import SubscriptionCancellationEmail from '../emails/templates/SubscriptionCancellationEmail';
import WelcomeEmail from '../emails/templates/WelcomeEmail';
import PasswordResetEmail from '../emails/templates/PasswordResetEmail';
import SubscriptionUpdateEmail from '../emails/templates/SubscriptionUpdateEmail';
import NewUserRegistrationEmail from '../emails/templates/NewUserRegistrationEmail';
import { renderEmailToHtml } from '../emails/renderEmail';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';
import { companyConfig } from '../emails/config/colors';
import { 
  Mail, 
  Settings, 
  Eye, 
  Code, 
  Send, 
  Copy, 
  Download, 
  Sparkles,
  FileText,
  Calendar,
  Bell,
  CreditCard,
  Package,
  AlertTriangle,
  Shield,
  Gift,
  Zap,
  RefreshCcw,
  Palette,
  Wand2,
  MailOpen,
  Play,
  Pause,
  ChevronDown
} from 'lucide-react';

// Define os tipos de templates disponíveis
type EmailTemplate = 'order-confirmation' | 'purchase-confirmation' | 'class-confirmation' | 'class-confirmation-v2' | 'class-confirmation-v3' | 'certificate-participation' | 'class-reminder' | 'class-cancellation' | 'invoice' | 'class-rescheduled' | 'subscription-cancellation' | 'welcome' | 'password-reset' | 'subscription-update' | 'new-user-registration';

// Define as props para cada tipo de template
interface EmailTemplateProps {
  'order-confirmation': React.ComponentProps<typeof OrderConfirmationEmail>;
  'purchase-confirmation': React.ComponentProps<typeof PurchaseConfirmationEmail>;
  'class-confirmation': React.ComponentProps<typeof ClassConfirmationEmail>;
  'class-confirmation-v2': React.ComponentProps<typeof ClassConfirmationEmailV2>;
  'class-confirmation-v3': React.ComponentProps<typeof ClassConfirmationEmailV3>;
  'certificate-participation': React.ComponentProps<typeof CertificateParticipationEmail>;
  'class-reminder': React.ComponentProps<typeof ClassReminderEmail>;
  'class-cancellation': React.ComponentProps<typeof ClassCancellationEmail>;
  'invoice': React.ComponentProps<typeof InvoiceEmail>;
  'class-rescheduled': React.ComponentProps<typeof ClassRescheduledEmail>;
  'subscription-cancellation': React.ComponentProps<typeof SubscriptionCancellationEmail>;
  'welcome': React.ComponentProps<typeof WelcomeEmail>;
  'password-reset': React.ComponentProps<typeof PasswordResetEmail>;
  'subscription-update': React.ComponentProps<typeof SubscriptionUpdateEmail>;
  'new-user-registration': React.ComponentProps<typeof NewUserRegistrationEmail>;
}

const ReactEmailPreview = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const { user } = useAuthContext();
  
  // Estado para o tipo de template selecionado
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate>('order-confirmation');
  
  // Estado para controle da interface
  const [activeView, setActiveView] = useState<'preview' | 'html'>('preview');
  
  // Função para obter informações visuais do template
  const getTemplateInfo = (template: EmailTemplate) => {
    const templateMap = {
      'order-confirmation': { icon: Package, color: 'blue', label: 'Confirmação de Pedido' },
      'purchase-confirmation': { icon: CreditCard, color: 'green', label: 'Confirmação de Compra' },
      'class-confirmation': { icon: Calendar, color: 'purple', label: 'Confirmação de Aula' },
      'class-confirmation-v2': { icon: Calendar, color: 'purple', label: 'Confirmação de Aula V2' },
      'class-confirmation-v3': { icon: Calendar, color: 'purple', label: 'Confirmação de Aula V3' },
      'certificate-participation': { icon: Gift, color: 'amber', label: 'Certificado de Participação' },
      'class-reminder': { icon: Bell, color: 'orange', label: 'Lembrete de Aula' },
      'class-cancellation': { icon: AlertTriangle, color: 'red', label: 'Cancelamento de Aula' },
      'invoice': { icon: FileText, color: 'indigo', label: 'Fatura' },
      'class-rescheduled': { icon: RefreshCcw, color: 'cyan', label: 'Aula Reagendada' },
      'subscription-cancellation': { icon: Package, color: 'red', label: 'Cancelamento de Assinatura' },
      'welcome': { icon: Sparkles, color: 'pink', label: 'Boas-vindas' },
      'password-reset': { icon: Shield, color: 'slate', label: 'Redefinição de Senha' },
      'subscription-update': { icon: Zap, color: 'yellow', label: 'Atualização de Assinatura' },
      'new-user-registration': { icon: Calendar, color: 'purple', label: 'Novo Usuário Cadastrado' }
    };
    return templateMap[template];
  };
  
  // Estados para as props de cada template
  const [orderConfirmationProps, setOrderConfirmationProps] = useState<EmailTemplateProps['order-confirmation']>({
    customerName: "João Silva",
    items: [
      {
        planName: "Plano Profissional",
        planDescription: "Acesso completo a todas as funcionalidades premium",
        price: "R$ 99,95",
        billingPeriod: "Mensal",
        nextBillingDate: "14 Abr, 2025",
      },
      {
        planName: "Módulo Adicional - Analytics",
        planDescription: "Relatórios avançados e análise de dados",
        price: "R$ 49,95",
        billingPeriod: "Mensal",
        nextBillingDate: "14 Abr, 2025",
      },
    ],
    transactionId: "2342",
    purchaseDate: "14 Mar, 2025",
    email: "joao.silva@email.com",
    total: "R$ 149,90"
  });
  
  const [purchaseConfirmationProps, setPurchaseConfirmationProps] = useState<EmailTemplateProps['purchase-confirmation']>({
    name: 'João Silva',
    company: 'SaaS Trivium',
    orderNumber: 'ORD-12345',
    orderDate: '10/06/2023',
    items: [
      { name: 'Plano Premium', quantity: 1, price: 399.00 },
      { name: 'Addon Integrações', quantity: 1, price: 99.00 },
    ],
    total: 498.00,
    paymentMethod: 'Cartão de Crédito',
    deliveryMethod: 'Acesso Digital',
    supportEmail: 'suporte@saasTrivium.com',
  });
  
  const [classConfirmationProps, setClassConfirmationProps] = useState<EmailTemplateProps['class-confirmation']>({
    name: 'João Silva',
    company: 'Trivium',
    classId: 'CLS-12345',
    courseName: 'Fundamentos de Analytics',
    instructorName: 'Dra. Ana Soares',
    locationAddress: 'Av. Paulista, 1000 - Sala 203, São Paulo - SP',
    startDate: '15 Abr, 2025 - 09:00',
    endDate: '15 Abr, 2025 - 17:00',
    status: 'confirmada',
    orgName: 'Empresa XYZ',
    domain: 'empresaxyz.com.br',
    industry: 'Tecnologia',
    supportEmail: 'suporte@trivium.com.br',
  });

  const [classConfirmationV2Props, setClassConfirmationV2Props] = useState<EmailTemplateProps['class-confirmation-v2']>({
    class_session_id: 'CS-789012',
    class_session_status: 'confirmed',
    start_date: '2025-04-15T09:00:00Z',
    end_date: '2025-04-15T17:00:00Z',
    duration_minutes: 480,
    teams_meeting_url: 'https://teams.microsoft.com/l/meetup-join/...',
    custom_meeting_url: '',
    meeting_url_type: 'teams',
    mode: 'hybrid',
    class_id: 'CLS-456',
    display_id: 'FA-2025-001',
    course_id: 'COURSE-123',
    name: 'Fundamentos de Data Analytics',
    family_name: 'Analytics Essentials',
    location_address: 'Av. Paulista, 1000 - Sala 203, São Paulo - SP',
    instructor_id: 'INST-001',
    instructor_name: 'Dra. Ana Carolina Soares',
    email: 'joao.silva@empresa.com.br',
    participant_name: 'João Silva',
    org_name: 'Empresa XYZ Ltda',
    domain: 'empresaxyz.com.br',
    industry: 'Tecnologia',
    subscription_id: 'SUB-789',
    subscription_start_date: '2025-01-01',
    subscription_end_date: '2025-12-31',
    session_count: 1,
    created_at: '2025-01-15T10:30:00Z',
  });

  const [classConfirmationV3Props, setClassConfirmationV3Props] = useState<EmailTemplateProps['class-confirmation-v3']>({
    class_session_id: 'CS-789012',
    class_session_status: 'confirmed',
    start_date: '2025-04-15T09:00:00Z',
    end_date: '2025-04-15T17:00:00Z',
    duration_minutes: 480,
    teams_meeting_url: 'https://teams.microsoft.com/l/meetup-join/...',
    event_calendar_id: 'CAL-123',
    meeting_id: 'MEET-456',
    custom_meeting_url: '',
    meeting_url_type: 'teams',
    mode: 'hybrid',
    checked_out: false,
    rescheduled_from: '',
    class_id: 'CLS-456',
    display_id: 'FA-2025-001',
    course_id: 'COURSE-123',
    name: 'Fundamentos de Data Analytics',
    family_name: 'Analytics Essentials',
    location_address: 'Av. Paulista, 1000 - Sala 203, São Paulo - SP',
    status: 'active',
    instructor_id: 'INST-001',
    instructor_name: 'Dra. Ana Carolina Soares',
    email: 'joao.silva@empresa.com.br',
    participant_name: 'João Silva',
    org_name: 'Empresa XYZ Ltda',
    domain: 'empresaxyz.com.br',
    industry: 'Tecnologia',
    subscription_id: 'SUB-789',
    subscription_start_date: '2025-01-01',
    subscription_end_date: '2025-12-31',
    state: 'active',
    canceled_at: '',
    session_count: 1,
    created_at: '2025-01-15T10:30:00Z',
    updated_at: '2025-01-15T10:30:00Z',
    created_by: '00000000-0000-0000-0000-000000000000',
    updated_by: '00000000-0000-0000-0000-000000000000',
  });
  
  const [certificateParticipationProps, setCertificateParticipationProps] = useState<EmailTemplateProps['certificate-participation']>({
    customerName: "João Silva",
    trainingName: "Análise Avançada de Dados",
    completionDate: "20 Abr, 2025",
    certificateId: "CERT-3842",
    instructorName: "Dra. Ana Martins",
    customerEmail: "joao.silva@email.com",
    hoursCompleted: "8 horas",
    certificateUrl: "https://trivium.com.br/certificates/CERT-3842",
    certificateValidUntil: "20 Abr, 2026",
  });
  
  const [classReminderProps, setClassReminderProps] = useState<EmailTemplateProps['class-reminder']>({
    name: 'João Silva',
    company: 'Trivium',
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
    classDetails: {
      name: 'Introdução ao TypeScript',
      date: '15/08/2023',
      time: '19:00',
      duration: '1 hora e 30 minutos',
      location: 'Online (Zoom)',
      instructor: 'Ana Souza',
      meetingLink: 'https://zoom.us/j/1234567890',
    },
    accountLink: 'https://trivium.com.br/minha-conta',
  });

  const [classCancellationProps, setClassCancellationProps] = useState<EmailTemplateProps['class-cancellation']>({
    name: 'João Silva',
    company: 'Trivium',
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
    classDetails: {
      name: 'Introdução ao TypeScript',
      date: '15/08/2023',
      time: '19:00',
      instructor: 'Ana Souza',
      reason: 'Problemas técnicos inesperados',
    },
    accountLink: 'https://trivium.com.br/minha-conta',
    rescheduleLink: 'https://trivium.com.br/reagendar',
  });

  const [invoiceProps, setInvoiceProps] = useState<EmailTemplateProps['invoice']>({
    name: 'João Silva',
    company: 'Trivium',
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
    invoice: {
      invoiceNumber: 'INV-2023-001',
      date: '01/08/2023',
      dueDate: '15/08/2023',
      amount: 'R$ 99,90',
      status: 'Pendente',
      items: [
        {
          description: 'Assinatura Premium - Mensal',
          quantity: 1,
          unitPrice: 'R$ 99,90',
          total: 'R$ 99,90'
        }
      ]
    },
    downloadLink: 'https://trivium.com.br/fatura/INV-2023-001/download',
    paymentLink: 'https://trivium.com.br/fatura/INV-2023-001/pagamento',
    accountLink: 'https://trivium.com.br/minha-conta/faturas',
  });

  const [classRescheduledProps, setClassRescheduledProps] = useState<EmailTemplateProps['class-rescheduled']>({
    name: 'João Silva',
    company: 'Trivium',
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
    classDetails: {
      name: 'Introdução ao TypeScript',
      oldDate: '15/08/2023',
      oldTime: '19:00',
      newDate: '18/08/2023',
      newTime: '14:00',
      duration: '1 hora e 30 minutos',
      location: 'Online (Zoom)',
      instructor: 'Ana Souza',
      meetingLink: 'https://zoom.us/j/1234567890',
      reasonForReschedule: 'Indisponibilidade do instrutor na data original',
    },
    accountLink: 'https://trivium.com.br/minha-conta',
    calendarLink: 'https://trivium.com.br/calendar/add?event=intro-typescript-18082023',
  });

  const [subscriptionCancellationProps, setSubscriptionCancellationProps] = useState<EmailTemplateProps['subscription-cancellation']>({
    name: 'João Silva',
    company: 'Trivium',
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
    subscription: {
      plan: 'Plano Premium',
      endDate: '15/08/2023',
      accessUntil: '31/08/2023'
    },
    reactivateLink: 'https://trivium.com.br/minha-conta/reativar',
    feedbackLink: 'https://trivium.com.br/feedback/cancelamento',
  });

  const [welcomeProps, setWelcomeProps] = useState<EmailTemplateProps['welcome']>({
    name: 'João Silva',
    company: 'Trivium',
    email: 'joao.silva@email.com',
    activationLink: 'https://trivium.com.br/activate/abc123',
    supportEmail: 'suporte@trivium.com.br',
    planName: 'Plano Profissional',
    domain: 'trivium.com.br',
  });

  const [passwordResetProps, setPasswordResetProps] = useState<EmailTemplateProps['password-reset']>({
    name: 'João Silva',
    company: 'Trivium',
    resetLink: 'https://trivium.com.br/reset-password/abc123',
    expirationHours: 24,
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
  });

  const [subscriptionUpdateProps, setSubscriptionUpdateProps] = useState<EmailTemplateProps['subscription-update']>({
    name: 'João Silva',
    company: 'Trivium',
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
    subscription: {
      updateType: 'increase',
      updateDate: '15/08/2023',
      effectiveDate: '15/08/2023',
      planChanges: [
        {
          planName: 'Licença Profissional',
          oldQuantity: 5,
          newQuantity: 10,
          pricePerUnit: 'R$ 99,90',
          oldTotal: 'R$ 499,50',
          newTotal: 'R$ 999,00'
        },
        {
          planName: 'Add-on: Analytics Avançado',
          oldQuantity: 2,
          newQuantity: 4,
          pricePerUnit: 'R$ 49,90',
          oldTotal: 'R$ 99,80',
          newTotal: 'R$ 199,60'
        }
      ],
      newBillingTotal: 'R$ 1.198,60',
      nextBillingDate: '15/09/2023'
    },
    accountLink: 'https://trivium.com.br/minha-conta',
    invoiceLink: 'https://trivium.com.br/minha-conta/faturas/atual',
  });

  const [newUserRegistrationProps, setNewUserRegistrationProps] = useState<EmailTemplateProps['new-user-registration']>({
    user_id: 'USR-12345',
    email: 'joao.silva@empresa.com.br',
    user_name: 'João Silva',
    registration_date: '2025-01-15T10:30:00Z',
    
    org_name: 'Empresa XYZ Ltda',
    domain: 'empresaxyz.com.br',
    industry: 'Tecnologia',
    
    subscription_id: 'SUB-789',
    plan_name: 'Plano Profissional',
    plan_type: 'premium',
    subscription_start_date: '2025-01-15',
    subscription_end_date: '2025-12-31',
    
    activation_link: 'https://trivium.com.br/activate/abc123',
    dashboard_link: 'https://trivium.com.br/dashboard',
    support_link: 'https://trivium.com.br/support',
    
    welcome_message: 'Seja bem-vindo à nossa plataforma de gestão educacional!',
    next_steps: [
      'Ative sua conta clicando no link de ativação',
      'Configure seu perfil e preferências',
      'Explore os recursos disponíveis em seu plano',
      'Convide sua equipe para colaborar'
    ],
    
    created_by: 'ADMIN-001',
    created_by_name: 'Maria Administradora',
    created_by_email: 'admin@empresaxyz.com.br',
  });
  
  // Estado para o HTML exportado
  const [exportedHtml, setExportedHtml] = useState<string>('');
  
  // Obtém as props do template atual
  const getCurrentTemplateProps = () => {
    switch (selectedTemplate) {
      case 'order-confirmation':
        return orderConfirmationProps;
      case 'purchase-confirmation':
        return purchaseConfirmationProps;
      case 'class-confirmation':
        return classConfirmationProps;
      case 'class-confirmation-v2':
        return classConfirmationV2Props;
      case 'class-confirmation-v3':
        return classConfirmationV3Props;
      case 'certificate-participation':
        return certificateParticipationProps;
      case 'class-reminder':
        return classReminderProps;
      case 'class-cancellation':
        return classCancellationProps;
      case 'invoice':
        return invoiceProps;
      case 'class-rescheduled':
        return classRescheduledProps;
      case 'subscription-cancellation':
        return subscriptionCancellationProps;
      case 'welcome':
        return welcomeProps;
      case 'password-reset':
        return passwordResetProps;
      case 'subscription-update':
        return subscriptionUpdateProps;
      case 'new-user-registration':
        return newUserRegistrationProps;
      default:
        return orderConfirmationProps;
    }
  };
  
  // Obtém o componente do template atual
  const getCurrentTemplateComponent = () => {
    switch (selectedTemplate) {
      case 'order-confirmation':
        return OrderConfirmationEmail;
      case 'purchase-confirmation':
        return PurchaseConfirmationEmail;
      case 'class-confirmation':
        return ClassConfirmationEmail;
      case 'class-confirmation-v2':
        return ClassConfirmationEmailV2;
      case 'class-confirmation-v3':
        return ClassConfirmationEmailV3;
      case 'certificate-participation':
        return CertificateParticipationEmail;
      case 'class-reminder':
        return ClassReminderEmail;
      case 'class-cancellation':
        return ClassCancellationEmail;
      case 'invoice':
        return InvoiceEmail;
      case 'class-rescheduled':
        return ClassRescheduledEmail;
      case 'subscription-cancellation':
        return SubscriptionCancellationEmail;
      case 'welcome':
        return WelcomeEmail;
      case 'password-reset':
        return PasswordResetEmail;
      case 'subscription-update':
        return SubscriptionUpdateEmail;
      case 'new-user-registration':
        return NewUserRegistrationEmail;
      default:
        return OrderConfirmationEmail;
    }
  };
  
  // Manipulador para mudanças nos inputs (genérico)
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setStateFn: React.Dispatch<React.SetStateAction<any>>
  ) => {
    const { name, value } = e.target;
    setStateFn(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  
  // Manipulador para mudança de template
  const handleTemplateChange = (value: string) => {
    setSelectedTemplate(value as EmailTemplate);
    setExportedHtml('');
  };
  
  // Exportar o HTML do template atual
  const handleExportHtml = () => {
    const component = getCurrentTemplateComponent();
    const props = getCurrentTemplateProps();
    const html = renderEmailToHtml(component, props);
    setExportedHtml(html);
  };
  
  // Copiar HTML para a área de transferência
  const copyHtmlToClipboard = () => {
    navigator.clipboard.writeText(exportedHtml);
    alert('HTML copiado para a área de transferência!');
  };

  // Renderiza campos de formulário com base no template selecionado
  const renderFormFields = () => {
    if (selectedTemplate === 'order-confirmation') {
      return (
        <div className="space-y-6">
          <div>
            <h4 className="font-extrabold text-gray-900 mb-3 flex items-center">
              <Package className="h-4 w-4 mr-2" />
              Dados do Pedido
            </h4>
            <div className="space-y-4">
          <div className="space-y-2">
                <Label htmlFor="customerName" className="text-sm font-extrabold text-gray-700">Nome do cliente</Label>
            <Input 
              id="customerName" 
              name="customerName" 
              value={orderConfirmationProps.customerName} 
              onChange={(e) => handleInputChange(e, setOrderConfirmationProps)} 
                  className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-extrabold text-gray-700">Email</Label>
            <Input 
              id="email" 
              name="email" 
                  type="email"
              value={orderConfirmationProps.email} 
              onChange={(e) => handleInputChange(e, setOrderConfirmationProps)} 
                  className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
              <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
                  <Label htmlFor="transactionId" className="text-sm font-extrabold text-gray-700">ID da Transação</Label>
            <Input 
              id="transactionId" 
              name="transactionId" 
              value={orderConfirmationProps.transactionId} 
              onChange={(e) => handleInputChange(e, setOrderConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="space-y-2">
                  <Label htmlFor="total" className="text-sm font-extrabold text-gray-700">Total</Label>
            <Input 
                    id="total" 
                    name="total" 
                    value={orderConfirmationProps.total} 
              onChange={(e) => handleInputChange(e, setOrderConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
                </div>
          </div>
          
          <div className="space-y-2">
                <Label htmlFor="purchaseDate" className="text-sm font-extrabold text-gray-700">Data da Compra</Label>
            <Input 
                  id="purchaseDate" 
                  name="purchaseDate" 
                  value={orderConfirmationProps.purchaseDate} 
              onChange={(e) => handleInputChange(e, setOrderConfirmationProps)} 
                  className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
            </div>
          </div>
        </div>
      );
    } else if (selectedTemplate === 'purchase-confirmation') {
      return (
        <div className="space-y-6">
          <div>
            <h4 className="font-extrabold text-gray-900 mb-3 flex items-center">
              <CreditCard className="h-4 w-4 mr-2" />
              Dados da Compra
            </h4>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-extrabold text-gray-700">Nome do cliente</Label>
            <Input 
              id="name" 
              name="name" 
              value={purchaseConfirmationProps.name} 
              onChange={(e) => handleInputChange(e, setPurchaseConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="space-y-2">
                  <Label htmlFor="company" className="text-sm font-extrabold text-gray-700">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={purchaseConfirmationProps.company} 
              onChange={(e) => handleInputChange(e, setPurchaseConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
                </div>
          </div>
          
              <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
                  <Label htmlFor="orderNumber" className="text-sm font-extrabold text-gray-700">Número do Pedido</Label>
            <Input 
              id="orderNumber" 
              name="orderNumber" 
              value={purchaseConfirmationProps.orderNumber} 
              onChange={(e) => handleInputChange(e, setPurchaseConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="space-y-2">
                  <Label htmlFor="orderDate" className="text-sm font-extrabold text-gray-700">Data do Pedido</Label>
            <Input 
              id="orderDate" 
              name="orderDate" 
              value={purchaseConfirmationProps.orderDate} 
              onChange={(e) => handleInputChange(e, setPurchaseConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
                </div>
          </div>
          
              <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
                  <Label htmlFor="paymentMethod" className="text-sm font-extrabold text-gray-700">Método de Pagamento</Label>
              <Input 
                id="paymentMethod" 
                name="paymentMethod" 
                value={purchaseConfirmationProps.paymentMethod} 
                onChange={(e) => handleInputChange(e, setPurchaseConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <div className="space-y-2">
                  <Label htmlFor="deliveryMethod" className="text-sm font-extrabold text-gray-700">Forma de Entrega</Label>
              <Input 
                id="deliveryMethod" 
                name="deliveryMethod" 
                value={purchaseConfirmationProps.deliveryMethod} 
                onChange={(e) => handleInputChange(e, setPurchaseConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
          
              <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
                  <Label htmlFor="total" className="text-sm font-extrabold text-gray-700">Valor Total</Label>
            <Input 
              id="total" 
              name="total" 
              type="number"
              value={purchaseConfirmationProps.total} 
              onChange={(e) => handleInputChange(e, setPurchaseConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="space-y-2">
                  <Label htmlFor="supportEmail" className="text-sm font-extrabold text-gray-700">Email de Suporte</Label>
            <Input 
              id="supportEmail" 
              name="supportEmail" 
                    type="email"
              value={purchaseConfirmationProps.supportEmail} 
              onChange={(e) => handleInputChange(e, setPurchaseConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (selectedTemplate === 'class-confirmation') {
      return (
        <div className="space-y-6">
          <div>
            <h4 className="font-extrabold text-gray-900 mb-3 flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              Dados da Aula
            </h4>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-extrabold text-gray-700">Nome do aluno</Label>
            <Input 
              id="name" 
              name="name" 
              value={classConfirmationProps.name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="space-y-2">
                  <Label htmlFor="company" className="text-sm font-extrabold text-gray-700">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={classConfirmationProps.company} 
              onChange={(e) => handleInputChange(e, setClassConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          </div>
          
          <div className="space-y-2">
                <Label htmlFor="courseName" className="text-sm font-extrabold text-gray-700">Nome do Curso</Label>
            <Input 
              id="courseName" 
              name="courseName" 
              value={classConfirmationProps.courseName} 
              onChange={(e) => handleInputChange(e, setClassConfirmationProps)} 
                  className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
              <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
                  <Label htmlFor="instructorName" className="text-sm font-extrabold text-gray-700">Nome do Instrutor</Label>
            <Input 
              id="instructorName" 
              name="instructorName" 
              value={classConfirmationProps.instructorName} 
              onChange={(e) => handleInputChange(e, setClassConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="space-y-2">
                  <Label htmlFor="classId" className="text-sm font-extrabold text-gray-700">ID da Aula</Label>
                  <Input 
                    id="classId" 
                    name="classId" 
                    value={classConfirmationProps.classId} 
                    onChange={(e) => handleInputChange(e, setClassConfirmationProps)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="locationAddress" className="text-sm font-extrabold text-gray-700">Endereço</Label>
            <Input 
              id="locationAddress" 
              name="locationAddress" 
              value={classConfirmationProps.locationAddress} 
              onChange={(e) => handleInputChange(e, setClassConfirmationProps)} 
                  className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
              <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm font-extrabold text-gray-700">Data/Hora de Início</Label>
              <Input 
                id="startDate" 
                name="startDate" 
                value={classConfirmationProps.startDate} 
                onChange={(e) => handleInputChange(e, setClassConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-sm font-extrabold text-gray-700">Data/Hora de Término</Label>
              <Input 
                id="endDate" 
                name="endDate" 
                value={classConfirmationProps.endDate} 
                onChange={(e) => handleInputChange(e, setClassConfirmationProps)} 
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
          </div>
          </div>
        </div>
      );
    } else if (selectedTemplate === 'class-confirmation-v2') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="participant_name">Nome do Participante</Label>
            <Input 
              id="participant_name" 
              name="participant_name" 
              value={classConfirmationV2Props.participant_name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="class_session_id">ID da Sessão</Label>
            <Input 
              id="class_session_id" 
              name="class_session_id" 
              value={classConfirmationV2Props.class_session_id} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="name">Nome do Curso</Label>
            <Input 
              id="name" 
              name="name" 
              value={classConfirmationV2Props.name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="family_name">Família do Curso</Label>
            <Input 
              id="family_name" 
              name="family_name" 
              value={classConfirmationV2Props.family_name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="instructor_name">Nome do Instrutor</Label>
            <Input 
              id="instructor_name" 
              name="instructor_name" 
              value={classConfirmationV2Props.instructor_name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="location_address">Endereço</Label>
            <Input 
              id="location_address" 
              name="location_address" 
              value={classConfirmationV2Props.location_address} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="start_date">Data/Hora de Início</Label>
              <Input 
                id="start_date" 
                name="start_date" 
                value={classConfirmationV2Props.start_date} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="end_date">Data/Hora de Término</Label>
              <Input 
                id="end_date" 
                name="end_date" 
                value={classConfirmationV2Props.end_date} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="duration_minutes">Duração (minutos)</Label>
            <Input 
              id="duration_minutes" 
              name="duration_minutes" 
              type="number"
              value={classConfirmationV2Props.duration_minutes} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="mode">Modalidade</Label>
            <Select value={classConfirmationV2Props.mode} onValueChange={(value) => 
              setClassConfirmationV2Props(prev => ({...prev, mode: value}))
            }>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a modalidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="presencial">Presencial</SelectItem>
                <SelectItem value="hybrid">Híbrido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="class_session_status">Status da Sessão</Label>
            <Select value={classConfirmationV2Props.class_session_status} onValueChange={(value) => 
              setClassConfirmationV2Props(prev => ({...prev, class_session_status: value}))
            }>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmada</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="cancelled">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="teams_meeting_url">URL da Reunião Teams</Label>
            <Input 
              id="teams_meeting_url" 
              name="teams_meeting_url" 
              value={classConfirmationV2Props.teams_meeting_url} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="org_name">Nome da Organização</Label>
            <Input 
              id="org_name" 
              name="org_name" 
              value={classConfirmationV2Props.org_name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="domain">Domínio</Label>
              <Input 
                id="domain" 
                name="domain" 
                value={classConfirmationV2Props.domain} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="industry">Indústria</Label>
              <Input 
                id="industry" 
                name="industry" 
                value={classConfirmationV2Props.industry} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV2Props)} 
              />
            </div>
          </div>
        </>
      );
    } else if (selectedTemplate === 'class-confirmation-v3') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="participant_name">Nome do Participante</Label>
            <Input 
              id="participant_name" 
              name="participant_name" 
              value={classConfirmationV3Props.participant_name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="class_session_id">ID da Sessão</Label>
            <Input 
              id="class_session_id" 
              name="class_session_id" 
              value={classConfirmationV3Props.class_session_id} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="name">Nome do Curso</Label>
            <Input 
              id="name" 
              name="name" 
              value={classConfirmationV3Props.name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="family_name">Família do Curso</Label>
            <Input 
              id="family_name" 
              name="family_name" 
              value={classConfirmationV3Props.family_name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="instructor_name">Nome do Instrutor</Label>
            <Input 
              id="instructor_name" 
              name="instructor_name" 
              value={classConfirmationV3Props.instructor_name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="location_address">Endereço</Label>
            <Input 
              id="location_address" 
              name="location_address" 
              value={classConfirmationV3Props.location_address} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="start_date">Data/Hora de Início</Label>
              <Input 
                id="start_date" 
                name="start_date" 
                value={classConfirmationV3Props.start_date} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="end_date">Data/Hora de Término</Label>
              <Input 
                id="end_date" 
                name="end_date" 
                value={classConfirmationV3Props.end_date} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="duration_minutes">Duração (minutos)</Label>
            <Input 
              id="duration_minutes" 
              name="duration_minutes" 
              type="number"
              value={classConfirmationV3Props.duration_minutes} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="mode">Modalidade</Label>
            <Select value={classConfirmationV3Props.mode} onValueChange={(value) => 
              setClassConfirmationV3Props(prev => ({...prev, mode: value}))
            }>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a modalidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="presencial">Presencial</SelectItem>
                <SelectItem value="hybrid">Híbrido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="class_session_status">Status da Sessão</Label>
            <Select value={classConfirmationV3Props.class_session_status} onValueChange={(value) => 
              setClassConfirmationV3Props(prev => ({...prev, class_session_status: value}))
            }>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmada</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="cancelled">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="teams_meeting_url">URL da Reunião Teams</Label>
            <Input 
              id="teams_meeting_url" 
              name="teams_meeting_url" 
              value={classConfirmationV3Props.teams_meeting_url} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="meeting_id">ID da Reunião</Label>
            <Input 
              id="meeting_id" 
              name="meeting_id" 
              value={classConfirmationV3Props.meeting_id} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="event_calendar_id">ID do Calendário</Label>
            <Input 
              id="event_calendar_id" 
              name="event_calendar_id" 
              value={classConfirmationV3Props.event_calendar_id} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="org_name">Nome da Organização</Label>
            <Input 
              id="org_name" 
              name="org_name" 
              value={classConfirmationV3Props.org_name} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="domain">Domínio</Label>
              <Input 
                id="domain" 
                name="domain" 
                value={classConfirmationV3Props.domain} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="industry">Indústria</Label>
              <Input 
                id="industry" 
                name="industry" 
                value={classConfirmationV3Props.industry} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subscription_id">ID da Assinatura</Label>
            <Input 
              id="subscription_id" 
              name="subscription_id" 
              value={classConfirmationV3Props.subscription_id} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="subscription_start_date">Data de Início da Assinatura</Label>
              <Input 
                id="subscription_start_date" 
                name="subscription_start_date" 
                value={classConfirmationV3Props.subscription_start_date} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subscription_end_date">Data de Fim da Assinatura</Label>
              <Input 
                id="subscription_end_date" 
                name="subscription_end_date" 
                value={classConfirmationV3Props.subscription_end_date} 
                onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="state">Status da Assinatura</Label>
            <Select value={classConfirmationV3Props.state} onValueChange={(value) => 
              setClassConfirmationV3Props(prev => ({...prev, state: value}))
            }>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativa</SelectItem>
                <SelectItem value="inactive">Inativa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="checked_out">Check-out</Label>
            <Select value={classConfirmationV3Props.checked_out?.toString()} onValueChange={(value) => 
              setClassConfirmationV3Props(prev => ({...prev, checked_out: value === 'true'}))
            }>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Sim</SelectItem>
                <SelectItem value="false">Não</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="rescheduled_from">Reagendada de</Label>
            <Input 
              id="rescheduled_from" 
              name="rescheduled_from" 
              value={classConfirmationV3Props.rescheduled_from} 
              onChange={(e) => handleInputChange(e, setClassConfirmationV3Props)} 
              placeholder="Deixe vazio se não for reagendada"
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'certificate-participation') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="customerName">Nome do Participante</Label>
            <Input 
              id="customerName" 
              name="customerName" 
              value={certificateParticipationProps.customerName} 
              onChange={(e) => handleInputChange(e, setCertificateParticipationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="customerEmail">Email do Participante</Label>
            <Input 
              id="customerEmail" 
              name="customerEmail" 
              value={certificateParticipationProps.customerEmail} 
              onChange={(e) => handleInputChange(e, setCertificateParticipationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="trainingName">Nome da Formação</Label>
            <Input 
              id="trainingName" 
              name="trainingName" 
              value={certificateParticipationProps.trainingName} 
              onChange={(e) => handleInputChange(e, setCertificateParticipationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="completionDate">Data de Conclusão</Label>
            <Input 
              id="completionDate" 
              name="completionDate" 
              value={certificateParticipationProps.completionDate} 
              onChange={(e) => handleInputChange(e, setCertificateParticipationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="hoursCompleted">Carga Horária</Label>
            <Input 
              id="hoursCompleted" 
              name="hoursCompleted" 
              value={certificateParticipationProps.hoursCompleted} 
              onChange={(e) => handleInputChange(e, setCertificateParticipationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="instructorName">Instrutor(a)</Label>
            <Input 
              id="instructorName" 
              name="instructorName" 
              value={certificateParticipationProps.instructorName} 
              onChange={(e) => handleInputChange(e, setCertificateParticipationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="certificateId">ID do Certificado</Label>
            <Input 
              id="certificateId" 
              name="certificateId" 
              value={certificateParticipationProps.certificateId} 
              onChange={(e) => handleInputChange(e, setCertificateParticipationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="certificateUrl">URL do Certificado</Label>
            <Input 
              id="certificateUrl" 
              name="certificateUrl" 
              value={certificateParticipationProps.certificateUrl} 
              onChange={(e) => handleInputChange(e, setCertificateParticipationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="certificateValidUntil">Válido até</Label>
            <Input 
              id="certificateValidUntil" 
              name="certificateValidUntil" 
              value={certificateParticipationProps.certificateValidUntil} 
              onChange={(e) => handleInputChange(e, setCertificateParticipationProps)} 
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'class-reminder') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="name">Nome do aluno</Label>
            <Input 
              id="name" 
              name="name" 
              value={classReminderProps.name} 
              onChange={(e) => handleInputChange(e, setClassReminderProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={classReminderProps.company} 
              onChange={(e) => handleInputChange(e, setClassReminderProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="className">Nome da Aula</Label>
            <Input 
              id="className" 
              name="classDetails.name" 
              value={classReminderProps.classDetails?.name} 
              onChange={(e) => {
                setClassReminderProps(prev => ({
                  ...prev,
                  classDetails: {
                    ...prev.classDetails!,
                    name: e.target.value
                  }
                }));
              }} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="classDate">Data da Aula</Label>
              <Input 
                id="classDate" 
                name="classDetails.date" 
                value={classReminderProps.classDetails?.date} 
                onChange={(e) => {
                  setClassReminderProps(prev => ({
                    ...prev,
                    classDetails: {
                      ...prev.classDetails!,
                      date: e.target.value
                    }
                  }));
                }} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="classTime">Horário</Label>
              <Input 
                id="classTime" 
                name="classDetails.time" 
                value={classReminderProps.classDetails?.time} 
                onChange={(e) => {
                  setClassReminderProps(prev => ({
                    ...prev,
                    classDetails: {
                      ...prev.classDetails!,
                      time: e.target.value
                    }
                  }));
                }} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="instructor">Instrutor</Label>
            <Input 
              id="instructor" 
              name="classDetails.instructor" 
              value={classReminderProps.classDetails?.instructor} 
              onChange={(e) => {
                setClassReminderProps(prev => ({
                  ...prev,
                  classDetails: {
                    ...prev.classDetails!,
                    instructor: e.target.value
                  }
                }));
              }} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="location">Local</Label>
            <Input 
              id="location" 
              name="classDetails.location" 
              value={classReminderProps.classDetails?.location} 
              onChange={(e) => {
                setClassReminderProps(prev => ({
                  ...prev,
                  classDetails: {
                    ...prev.classDetails!,
                    location: e.target.value
                  }
                }));
              }} 
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'class-cancellation') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="name">Nome do aluno</Label>
            <Input 
              id="name" 
              name="name" 
              value={classCancellationProps.name} 
              onChange={(e) => handleInputChange(e, setClassCancellationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={classCancellationProps.company} 
              onChange={(e) => handleInputChange(e, setClassCancellationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="className">Nome da Aula</Label>
            <Input 
              id="className" 
              name="classDetails.name" 
              value={classCancellationProps.classDetails?.name} 
              onChange={(e) => {
                setClassCancellationProps(prev => ({
                  ...prev,
                  classDetails: {
                    ...prev.classDetails!,
                    name: e.target.value
                  }
                }));
              }} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="classDate">Data da Aula</Label>
              <Input 
                id="classDate" 
                name="classDetails.date" 
                value={classCancellationProps.classDetails?.date} 
                onChange={(e) => {
                  setClassCancellationProps(prev => ({
                    ...prev,
                    classDetails: {
                      ...prev.classDetails!,
                      date: e.target.value
                    }
                  }));
                }} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="classTime">Horário</Label>
              <Input 
                id="classTime" 
                name="classDetails.time" 
                value={classCancellationProps.classDetails?.time} 
                onChange={(e) => {
                  setClassCancellationProps(prev => ({
                    ...prev,
                    classDetails: {
                      ...prev.classDetails!,
                      time: e.target.value
                    }
                  }));
                }} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="reason">Motivo do Cancelamento</Label>
            <Input 
              id="reason" 
              name="classDetails.reason" 
              value={classCancellationProps.classDetails?.reason} 
              onChange={(e) => {
                setClassCancellationProps(prev => ({
                  ...prev,
                  classDetails: {
                    ...prev.classDetails!,
                    reason: e.target.value
                  }
                }));
              }} 
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'invoice') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="name">Nome do cliente</Label>
            <Input 
              id="name" 
              name="name" 
              value={invoiceProps.name} 
              onChange={(e) => handleInputChange(e, setInvoiceProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={invoiceProps.company} 
              onChange={(e) => handleInputChange(e, setInvoiceProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="invoiceNumber">Número da Fatura</Label>
            <Input 
              id="invoiceNumber" 
              name="invoice.invoiceNumber" 
              value={invoiceProps.invoice?.invoiceNumber} 
              onChange={(e) => {
                setInvoiceProps(prev => ({
                  ...prev,
                  invoice: {
                    ...prev.invoice!,
                    invoiceNumber: e.target.value
                  }
                }));
              }} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="issueDate">Data de Emissão</Label>
              <Input 
                id="issueDate" 
                name="invoice.date" 
                value={invoiceProps.invoice?.date} 
                onChange={(e) => {
                  setInvoiceProps(prev => ({
                    ...prev,
                    invoice: {
                      ...prev.invoice!,
                      date: e.target.value
                    }
                  }));
                }} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="dueDate">Data de Vencimento</Label>
              <Input 
                id="dueDate" 
                name="invoice.dueDate" 
                value={invoiceProps.invoice?.dueDate} 
                onChange={(e) => {
                  setInvoiceProps(prev => ({
                    ...prev,
                    invoice: {
                      ...prev.invoice!,
                      dueDate: e.target.value
                    }
                  }));
                }} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="amount">Valor Total</Label>
            <Input 
              id="amount" 
              name="invoice.amount" 
              value={invoiceProps.invoice?.amount} 
              onChange={(e) => {
                setInvoiceProps(prev => ({
                  ...prev,
                  invoice: {
                    ...prev.invoice!,
                    amount: e.target.value
                  }
                }));
              }} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Input 
              id="status" 
              name="invoice.status" 
              value={invoiceProps.invoice?.status} 
              onChange={(e) => {
                setInvoiceProps(prev => ({
                  ...prev,
                  invoice: {
                    ...prev.invoice!,
                    status: e.target.value
                  }
                }));
              }} 
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'class-rescheduled') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="name">Nome do aluno</Label>
            <Input 
              id="name" 
              name="name" 
              value={classRescheduledProps.name} 
              onChange={(e) => handleInputChange(e, setClassRescheduledProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={classRescheduledProps.company} 
              onChange={(e) => handleInputChange(e, setClassRescheduledProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="className">Nome da Aula</Label>
            <Input 
              id="className" 
              name="classDetails.name" 
              value={classRescheduledProps.classDetails?.name} 
              onChange={(e) => {
                setClassRescheduledProps(prev => ({
                  ...prev,
                  classDetails: {
                    ...prev.classDetails!,
                    name: e.target.value
                  }
                }));
              }} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="oldDate">Data Original</Label>
              <Input 
                id="oldDate" 
                name="classDetails.oldDate" 
                value={classRescheduledProps.classDetails?.oldDate} 
                onChange={(e) => {
                  setClassRescheduledProps(prev => ({
                    ...prev,
                    classDetails: {
                      ...prev.classDetails!,
                      oldDate: e.target.value
                    }
                  }));
                }} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="oldTime">Horário Original</Label>
              <Input 
                id="oldTime" 
                name="classDetails.oldTime" 
                value={classRescheduledProps.classDetails?.oldTime} 
                onChange={(e) => {
                  setClassRescheduledProps(prev => ({
                    ...prev,
                    classDetails: {
                      ...prev.classDetails!,
                      oldTime: e.target.value
                    }
                  }));
                }} 
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="newDate">Nova Data</Label>
              <Input 
                id="newDate" 
                name="classDetails.newDate" 
                value={classRescheduledProps.classDetails?.newDate} 
                onChange={(e) => {
                  setClassRescheduledProps(prev => ({
                    ...prev,
                    classDetails: {
                      ...prev.classDetails!,
                      newDate: e.target.value
                    }
                  }));
                }} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newTime">Novo Horário</Label>
              <Input 
                id="newTime" 
                name="classDetails.newTime" 
                value={classRescheduledProps.classDetails?.newTime} 
                onChange={(e) => {
                  setClassRescheduledProps(prev => ({
                    ...prev,
                    classDetails: {
                      ...prev.classDetails!,
                      newTime: e.target.value
                    }
                  }));
                }} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="reasonForReschedule">Motivo do Reagendamento</Label>
            <Input 
              id="reasonForReschedule" 
              name="classDetails.reasonForReschedule" 
              value={classRescheduledProps.classDetails?.reasonForReschedule} 
              onChange={(e) => {
                setClassRescheduledProps(prev => ({
                  ...prev,
                  classDetails: {
                    ...prev.classDetails!,
                    reasonForReschedule: e.target.value
                  }
                }));
              }} 
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'subscription-cancellation') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="name">Nome do cliente</Label>
            <Input 
              id="name" 
              name="name" 
              value={subscriptionCancellationProps.name} 
              onChange={(e) => handleInputChange(e, setSubscriptionCancellationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={subscriptionCancellationProps.company} 
              onChange={(e) => handleInputChange(e, setSubscriptionCancellationProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="plan">Plano</Label>
            <Input 
              id="plan" 
              name="subscription.plan" 
              value={subscriptionCancellationProps.subscription?.plan} 
              onChange={(e) => {
                setSubscriptionCancellationProps(prev => ({
                  ...prev,
                  subscription: {
                    ...prev.subscription!,
                    plan: e.target.value
                  }
                }));
              }} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="endDate">Data de Cancelamento</Label>
              <Input 
                id="endDate" 
                name="subscription.endDate" 
                value={subscriptionCancellationProps.subscription?.endDate} 
                onChange={(e) => {
                  setSubscriptionCancellationProps(prev => ({
                    ...prev,
                    subscription: {
                      ...prev.subscription!,
                      endDate: e.target.value
                    }
                  }));
                }} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="accessUntil">Acesso até</Label>
              <Input 
                id="accessUntil" 
                name="subscription.accessUntil" 
                value={subscriptionCancellationProps.subscription?.accessUntil} 
                onChange={(e) => {
                  setSubscriptionCancellationProps(prev => ({
                    ...prev,
                    subscription: {
                      ...prev.subscription!,
                      accessUntil: e.target.value
                    }
                  }));
                }} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="reactivateLink">Link para Reativação</Label>
            <Input 
              id="reactivateLink" 
              name="reactivateLink" 
              value={subscriptionCancellationProps.reactivateLink} 
              onChange={(e) => handleInputChange(e, setSubscriptionCancellationProps)} 
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'welcome') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="name">Nome do cliente</Label>
            <Input 
              id="name" 
              name="name" 
              value={welcomeProps.name} 
              onChange={(e) => handleInputChange(e, setWelcomeProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email" 
              name="email" 
              value={welcomeProps.email} 
              onChange={(e) => handleInputChange(e, setWelcomeProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={welcomeProps.company} 
              onChange={(e) => handleInputChange(e, setWelcomeProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="planName">Plano</Label>
            <Input 
              id="planName" 
              name="planName" 
              value={welcomeProps.planName} 
              onChange={(e) => handleInputChange(e, setWelcomeProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="activationLink">Link de Ativação</Label>
            <Input 
              id="activationLink" 
              name="activationLink" 
              value={welcomeProps.activationLink} 
              onChange={(e) => handleInputChange(e, setWelcomeProps)} 
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'password-reset') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="name">Nome do cliente</Label>
            <Input 
              id="name" 
              name="name" 
              value={passwordResetProps.name} 
              onChange={(e) => handleInputChange(e, setPasswordResetProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={passwordResetProps.company} 
              onChange={(e) => handleInputChange(e, setPasswordResetProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="resetLink">Link de Redefinição</Label>
            <Input 
              id="resetLink" 
              name="resetLink" 
              value={passwordResetProps.resetLink} 
              onChange={(e) => handleInputChange(e, setPasswordResetProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="expirationHours">Horas para Expiração</Label>
            <Input 
              id="expirationHours" 
              name="expirationHours" 
              type="number"
              value={passwordResetProps.expirationHours} 
              onChange={(e) => handleInputChange(e, setPasswordResetProps)} 
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'subscription-update') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="name">Nome do cliente</Label>
            <Input 
              id="name" 
              name="name" 
              value={subscriptionUpdateProps.name} 
              onChange={(e) => handleInputChange(e, setSubscriptionUpdateProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input 
              id="company" 
              name="company" 
              value={subscriptionUpdateProps.company} 
              onChange={(e) => handleInputChange(e, setSubscriptionUpdateProps)} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="updateType">Tipo de Atualização</Label>
            <Select
              value={subscriptionUpdateProps.subscription?.updateType}
              onValueChange={(value) => {
                setSubscriptionUpdateProps(prev => ({
                  ...prev,
                  subscription: {
                    ...prev.subscription!,
                    updateType: value as 'increase' | 'decrease'
                  }
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="increase">Aumento de Licenças</SelectItem>
                <SelectItem value="decrease">Redução de Licenças</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="updateDate">Data da Atualização</Label>
              <Input 
                id="updateDate" 
                name="subscription.updateDate" 
                value={subscriptionUpdateProps.subscription?.updateDate} 
                onChange={(e) => {
                  setSubscriptionUpdateProps(prev => ({
                    ...prev,
                    subscription: {
                      ...prev.subscription!,
                      updateDate: e.target.value
                    }
                  }));
                }} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="effectiveDate">Data de Efetivação</Label>
              <Input 
                id="effectiveDate" 
                name="subscription.effectiveDate" 
                value={subscriptionUpdateProps.subscription?.effectiveDate} 
                onChange={(e) => {
                  setSubscriptionUpdateProps(prev => ({
                    ...prev,
                    subscription: {
                      ...prev.subscription!,
                      effectiveDate: e.target.value
                    }
                  }));
                }} 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="newBillingTotal">Novo Total</Label>
            <Input 
              id="newBillingTotal" 
              name="subscription.newBillingTotal" 
              value={subscriptionUpdateProps.subscription?.newBillingTotal} 
              onChange={(e) => {
                setSubscriptionUpdateProps(prev => ({
                  ...prev,
                  subscription: {
                    ...prev.subscription!,
                    newBillingTotal: e.target.value
                  }
                }));
              }} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="nextBillingDate">Próxima Fatura</Label>
            <Input 
              id="nextBillingDate" 
              name="subscription.nextBillingDate" 
              value={subscriptionUpdateProps.subscription?.nextBillingDate} 
              onChange={(e) => {
                setSubscriptionUpdateProps(prev => ({
                  ...prev,
                  subscription: {
                    ...prev.subscription!,
                    nextBillingDate: e.target.value
                  }
                }));
              }} 
            />
          </div>
        </>
      );
    } else if (selectedTemplate === 'new-user-registration') {
      return (
        <div className="space-y-6">
          <div>
            <h4 className="font-extrabold text-gray-900 mb-3 flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              Novo Usuário Cadastrado
            </h4>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="user_name" className="text-sm font-extrabold text-gray-700">Nome do usuário</Label>
                  <Input 
                    id="user_name" 
                    name="user_name" 
                    value={newUserRegistrationProps.user_name} 
                    onChange={(e) => handleInputChange(e, setNewUserRegistrationProps)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-extrabold text-gray-700">Email</Label>
                  <Input 
                    id="email" 
                    name="email" 
                    type="email"
                    value={newUserRegistrationProps.email} 
                    onChange={(e) => handleInputChange(e, setNewUserRegistrationProps)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="org_name" className="text-sm font-extrabold text-gray-700">Organização</Label>
                  <Input 
                    id="org_name" 
                    name="org_name" 
                    value={newUserRegistrationProps.org_name} 
                    onChange={(e) => handleInputChange(e, setNewUserRegistrationProps)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="plan_name" className="text-sm font-extrabold text-gray-700">Plano</Label>
                  <Input 
                    id="plan_name" 
                    name="plan_name" 
                    value={newUserRegistrationProps.plan_name} 
                    onChange={(e) => handleInputChange(e, setNewUserRegistrationProps)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="user_id" className="text-sm font-extrabold text-gray-700">ID do Usuário</Label>
                  <Input 
                    id="user_id" 
                    name="user_id" 
                    value={newUserRegistrationProps.user_id} 
                    onChange={(e) => handleInputChange(e, setNewUserRegistrationProps)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="plan_type" className="text-sm font-extrabold text-gray-700">Tipo de Plano</Label>
                  <Input 
                    id="plan_type" 
                    name="plan_type" 
                    value={newUserRegistrationProps.plan_type} 
                    onChange={(e) => handleInputChange(e, setNewUserRegistrationProps)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="activation_link" className="text-sm font-extrabold text-gray-700">Link de Ativação</Label>
                <Input 
                  id="activation_link" 
                  name="activation_link" 
                  value={newUserRegistrationProps.activation_link} 
                  onChange={(e) => handleInputChange(e, setNewUserRegistrationProps)}
                  className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="created_by_name" className="text-sm font-extrabold text-gray-700">Criado por</Label>
                  <Input 
                    id="created_by_name" 
                    name="created_by_name" 
                    value={newUserRegistrationProps.created_by_name} 
                    onChange={(e) => handleInputChange(e, setNewUserRegistrationProps)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="industry" className="text-sm font-extrabold text-gray-700">Indústria</Label>
                  <Input 
                    id="industry" 
                    name="industry" 
                    value={newUserRegistrationProps.industry} 
                    onChange={(e) => handleInputChange(e, setNewUserRegistrationProps)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Fallback para outros templates com estilo padrão moderno
    return (
      <div className="space-y-6">
        <div>
          <h4 className="font-extrabold text-gray-900 mb-3 flex items-center">
            <IconComponent className="h-4 w-4 mr-2" />
            Configurações do Template
          </h4>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                Os campos de configuração para este template estão disponíveis no formato original.
                <br />
                <span className="text-xs text-gray-500">
                  Selecione um template diferente para ver a nova interface modernizada.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Função para enviar um email de teste
  const sendTestEmail = async () => {
    if (!testEmail) {
      toast({
        title: 'Erro',
        description: 'Por favor, informe um email para teste.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Gerar o HTML do template
      const templateComponent = getCurrentTemplateComponent();
      const templateProps = getCurrentTemplateProps();
      const htmlContent = renderEmailToHtml(templateComponent, templateProps);

      // Determinar subject com base no template selecionado
      let subject = 'Teste de Email';
      
      if (selectedTemplate === 'order-confirmation') {
        subject = `Confirmação de Pedido #${orderConfirmationProps.transactionId}`;
      } else if (selectedTemplate === 'purchase-confirmation') {
        subject = `Confirmação de Compra - ${purchaseConfirmationProps.name}`;
      } else if (selectedTemplate === 'class-confirmation') {
        subject = `Confirmação de Aula - ${classConfirmationProps.courseName}`;
      } else if (selectedTemplate === 'class-confirmation-v2') {
        subject = `Confirmação de Aula - ${classConfirmationV2Props.name}`;
      } else if (selectedTemplate === 'class-confirmation-v3') {
        subject = `Confirmação de Aula - ${classConfirmationV3Props.name}`;
      } else if (selectedTemplate === 'certificate-participation') {
        subject = `Certificado de Participação - ${certificateParticipationProps.trainingName}`;
      } else if (selectedTemplate === 'class-reminder') {
        subject = `Lembrete de Aula - ${classReminderProps.classDetails?.name}`;
      } else if (selectedTemplate === 'class-cancellation') {
        subject = `Cancelamento de Aula - ${classCancellationProps.classDetails?.name}`;
      } else if (selectedTemplate === 'invoice') {
        subject = `Fatura #${invoiceProps.invoice?.invoiceNumber}`;
      } else if (selectedTemplate === 'class-rescheduled') {
        subject = `Aula Reagendada - ${classRescheduledProps.classDetails?.name}`;
      } else if (selectedTemplate === 'subscription-cancellation') {
        subject = `Cancelamento de Assinatura - ${subscriptionCancellationProps.subscription?.plan}`;
      } else if (selectedTemplate === 'welcome') {
        subject = `Bem-vindo à ${welcomeProps.company}!`;
      } else if (selectedTemplate === 'password-reset') {
        subject = `Redefinição de Senha - ${passwordResetProps.company}`;
      } else if (selectedTemplate === 'subscription-update') {
        subject = `Atualização de Assinatura - ${subscriptionUpdateProps.company}`;
      } else if (selectedTemplate === 'new-user-registration') {
        subject = `Bem-vindo à ${companyConfig?.name || 'Plataforma'} - ${newUserRegistrationProps.user_name}`;
      }

      // Chamar Edge Function para enviar o email
      const { data: supabaseConfig } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = supabaseConfig.session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_email: testEmail,
          subject: subject,
          body: htmlContent,
          test_mode: true // Modo teste para redirecionar para email de teste
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Erro na Edge Function: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success > 0) {
        toast({
          title: 'Sucesso',
          description: 'Email de teste enviado com sucesso via Edge Function!',
        });
      } else if (result.failed > 0) {
        throw new Error(`Falha no envio: ${result.errors?.join(', ') || 'Erro desconhecido'}`);
      } else {
        throw new Error('Nenhum email foi processado');
      }

    } catch (error) {
      console.error('Erro ao enviar email de teste:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Ocorreu um erro ao enviar o email de teste.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const currentTemplateInfo = getTemplateInfo(selectedTemplate);
  const IconComponent = currentTemplateInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 pl-3 lg:pl-4">
      {/* Modern Header with Gradient Background */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">Email Templates</h1>
                  <p className="text-blue-100 text-base pb-2">
                    Visualize, personalize e teste seus templates de email
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="default" 
                onClick={handleExportHtml}
                className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar HTML
              </Button>
              <Button 
                size="default" 
                onClick={sendTestEmail}
                disabled={isLoading}
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-gray-900 rounded-full border-t-transparent mr-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar Teste
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">
        {/* Template Selection Card */}
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4 flex-1">
                <div className="min-w-0 flex-1">
                  <Label htmlFor="template-select" className="text-sm font-extrabold mb-2 block">
                    Selecionar Template
                  </Label>
                  <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                    <SelectTrigger className="h-12 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue placeholder="Selecione um template" />
                </SelectTrigger>
                <SelectContent>
                      {[
                        'order-confirmation', 'purchase-confirmation', 'class-confirmation', 
                        'class-confirmation-v2', 'class-confirmation-v3', 'certificate-participation',
                        'class-reminder', 'class-cancellation', 'class-rescheduled', 'invoice',
                        'subscription-cancellation', 'subscription-update', 'welcome', 'password-reset',
                        'new-user-registration'
                      ].map((template) => {
                        const info = getTemplateInfo(template as EmailTemplate);
                        const TemplateIcon = info.icon;
                        return (
                          <SelectItem key={template} value={template} className="py-3">
                            <div className="flex items-center gap-3">
                              <div className={`p-1.5 bg-${info.color}-100 rounded-md`}>
                                <TemplateIcon className={`h-3 w-3 text-${info.color}-600`} />
                              </div>
                              <span className="font-extrabold">{info.label}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                </SelectContent>
              </Select>
            </div>
            
                <div className="relative min-w-0 flex-1 max-w-sm">
                  <Label htmlFor="test-email" className="text-sm font-extrabold mb-2 block">
                    Email para Teste
                  </Label>
                  <Input 
                    id="test-email" 
                    type="email" 
                    placeholder="seu@email.com" 
                    value={testEmail} 
                    onChange={(e) => setTestEmail(e.target.value)} 
                    className="h-12 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                </div>
                
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveView('preview')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeView === 'preview'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </button>
                <button
                  onClick={() => setActiveView('html')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeView === 'html'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Code className="h-4 w-4" />
                  HTML
                </button>
                </div>
              </div>

            {/* Current Template Info Badge */}
            <div className="flex items-center gap-2 mb-6">
              <Badge 
                variant="outline" 
                className={`bg-${currentTemplateInfo.color}-50 text-${currentTemplateInfo.color}-700 border-${currentTemplateInfo.color}-200 px-3 py-1`}
              >
                <IconComponent className="h-3 w-3 mr-2" />
                {currentTemplateInfo.label}
              </Badge>
              <span className="text-sm text-gray-500">Template selecionado</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Configuration Panel */}
          <Card className="xl:col-span-1 border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Configurações
              </CardTitle>
            <CardDescription>
                Personalize os dados do template
            </CardDescription>
          </CardHeader>
            <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
              {renderFormFields()}
            </CardContent>
          </Card>

          {/* Preview/HTML Panel */}
          <Card className="xl:col-span-3 border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center">
                {activeView === 'preview' ? (
                  <>
                    <Eye className="h-5 w-5 mr-2" />
                    Preview do Email
                  </>
                ) : (
                  <>
                    <Code className="h-5 w-5 mr-2" />
                    Código HTML
                  </>
                )}
              </CardTitle>
              <CardDescription>
                {activeView === 'preview' 
                  ? 'Visualização em tempo real do template' 
                  : 'Código HTML gerado do template'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeView === 'preview' ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                <EmailPreview 
                  EmailComponent={getCurrentTemplateComponent()} 
                  props={getCurrentTemplateProps()} 
                />
                </div>
              ) : (
                <div className="space-y-4">
                {exportedHtml ? (
                    <>
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            HTML Gerado
                          </Badge>
                          <span className="text-sm text-gray-600">
                            {exportedHtml.length} caracteres
                          </span>
                        </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={copyHtmlToClipboard}
                          className="flex items-center gap-2"
                      >
                          <Copy className="h-4 w-4" />
                          Copiar
                      </Button>
                    </div>
                      <div className="border border-gray-200 rounded-lg bg-gray-900 p-4 max-h-[500px] overflow-auto">
                        <pre className="text-xs text-green-400 font-mono leading-relaxed">
                      {exportedHtml}
                    </pre>
                  </div>
                    </>
                  ) : (
                    <div className="text-center py-20">
                      <div className="space-y-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                          <Code className="h-8 w-8 text-blue-500" />
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-lg font-extrabold text-gray-900">
                            HTML não gerado
                          </h3>
                          <p className="text-gray-500 max-w-md mx-auto">
                            Clique em "Exportar HTML" para gerar e visualizar o código do template.
                          </p>
                        </div>
                        <Button onClick={handleExportHtml} size="lg">
                          <Wand2 className="h-5 w-5 mr-2" />
                          Gerar HTML
                        </Button>
                      </div>
                  </div>
                )}
                </div>
              )}
          </CardContent>
        </Card>
        </div>
        
        {/* Bottom Spacing */}
        <div className="h-16" />
      </div>
    </div>
  );
};

export default ReactEmailPreview; 